import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { Command, GameState } from "src/engine/rules/commands";
import { isAttackedByEnemyOf } from "src/engine/rules/chess/attacks";
import {
  MECHANICS_VERSION,
  type MechanicId,
  mechanicsOfLog
} from "src/engine/telemetry/mechanics";
import {
  type Concept,
  conceptById,
  isLearned,
  nextHint,
  type Quiet
} from "src/engine/telemetry/curriculum";
import {
  loadProfile,
  onProfileReset,
  type Profile,
  saveProfile
} from "src/engine/telemetry/profile";
import { HINTS, type HintText } from "src/client/ai/content/hints/en";
import { type HintTarget, hintTargetOf } from "./hintTargets";

export interface Hint {
  concept: Concept;
  text: HintText;
  /**
   * The square or control the hint is drawn at, resolved here rather than by
   * the overlay: this hook is the one place that already holds the board and
   * the side, and two answers to "which pawn is this about" is two rings.
   */
  target: HintTarget | null;
  /** Never tell me this again — the × . Recorded in the profile. */
  dismiss(): void;
  /**
   * Not now — a click anywhere else on the board.
   *
   * **Deliberately not `dismiss`.** A player clicking past a card to reach the
   * square underneath it has said where they want to click and nothing at all
   * about the concept, and spending a concept on a stray click means the one
   * hint they most needed is the one they will never see again. It clears the
   * card and takes the turn's offer with it, so the curriculum comes back to
   * this on the next turn if it is still the thing to learn.
   */
  hide(): void;
}

/**
 * The curriculum, paced (§16.3.2).
 *
 * > **At most one new concept is offered per turn**, and **a concept is not
 * > replaced until it has been used `learned` times.**
 *
 * The second rule is what makes the first mean anything. "One a turn" on its
 * own is a firehose with a delay on it: ten turns in, ten concepts have gone
 * past and the player has done none of them. Held to the current one until it
 * is used, the curriculum moves at the speed of the player — somebody who
 * mines on turn two meets belts on turn three, and somebody who does not is
 * still being told about mining on turn nine, which is correct.
 *
 * **Per turn, not per move.** With §13's Titan `movesPerTurn` is above one, and
 * a concept per *move* would be three a turn exactly when the player is
 * busiest.
 *
 * The pacing lives here rather than in `curriculum.ts` because it is state
 * about a session, not a fact about the game: `nextHint` stays a pure function
 * of a profile and a board, which is what lets the whole table be graded by
 * replaying a log.
 */
export function useHints(
  state: GameState,
  /**
   * The command log, which is where the profile comes from. Passed in rather
   * than read off the state: the log is the store's, and a hook that reached
   * for the store would be a second definition of which game is being played.
   */
  log: readonly Command[],
  side: number,
  options: { watching?: boolean; busy?: boolean } = {}
): Hint | null {
  const [profile, setProfile] = useState<Profile>(() => loadProfile());
  /** The concept on screen, held until it is used or dismissed. */
  const [showing, setShowing] = useState<MechanicId | null>(null);
  /** The turn the last offer was made on, so it is one a turn. */
  const offered = useRef(-1);

  // **The profile is rebuilt from the log, never incremented.** An increment
  // has to be right on every path and undo is truncate-and-replay, so it would
  // have to be un-incremented too; a reduction has nothing to drift from.
  useEffect(() => {
    const counts = mechanicsOfLog(log);
    setProfile((was) => {
      const ids = Object.keys(counts) as MechanicId[];
      const same =
        ids.length === Object.keys(was.counts).length &&
        ids.every((id) => was.counts[id] === counts[id]);
      return same ? was : { ...was, version: MECHANICS_VERSION, counts };
    });
  }, [log]);

  // The dev control gives every dismissed concept back, and it has to land on
  // a hook that is already holding a copy — otherwise the only honest label
  // for that button is "reload to see it fresh". `offered` goes back too, or
  // the curriculum sits out the turn the reset happened on.
  useEffect(
    () =>
      onProfileReset(() => {
        offered.current = -1;
        setShowing(null);
        setProfile(loadProfile());
      }),
    []
  );

  const dismiss = useCallback(() => {
    setShowing((current) => {
      if (current) {
        setProfile((was) => {
          if (was.dismissed?.includes(current)) return was;
          const next = { ...was, dismissed: [...(was.dismissed ?? []), current] };
          saveProfile(next);
          return next;
        });
      }
      return null;
    });
  }, []);

  const turn = state.industry.turnCounter;

  // **The turn is spent, so the next offer waits for the clock.** Without it
  // the effect below re-offers the same concept on the very next render — the
  // board has not changed, so `nextHint` gives the same answer — and clicking
  // a card away would put it straight back.
  const hide = useCallback(() => {
    offered.current = turn;
    setShowing(null);
  }, [turn]);
  const off = profile.hints === false;
  const busy = Boolean(options.busy);
  const watching = Boolean(options.watching);

  // **Resolved in an effect, not during render.** The decision reads the board
  // *and* writes which concept is held, and a render that does both loops the
  // moment `when` disagrees with itself between two renders of the same turn.
  useEffect(() => {
    const quiet: Quiet = {
      // §16.3.4, and the first rule of it: introducing a mechanic on the turn
      // the player is fighting for their king is teaching at the worst
      // possible moment — the same instinct that makes a march yield in check.
      inCheck: isAttackedByEnemyOf(
        state.position,
        state.position.kings[state.position.turn] ?? -1,
        state.position.turn
      ),
      busy,
      watching,
      off
    };

    setShowing((current) => {
      const held = current ? conceptById(current) : undefined;
      // **A concept is not replaced until it has been used.** It is dropped
      // when the board stops making it true as well, or a hint outlives the
      // thing it is about — "mine the heap" with every heap worked out.
      if (held && !quiet.off && !isLearned(profile, held) && held.when(state, side)) {
        return current;
      }
      // Letting one go still spends the turn, so the next waits for the clock.
      if (held) {
        offered.current = turn;
        return null;
      }
      if (offered.current === turn) return null;
      const offer = nextHint(profile, state, side, quiet);
      if (!offer || !HINTS[offer.id]) return null;
      offered.current = turn;
      return offer.id;
    });
  }, [state, side, profile, turn, busy, watching, off]);

  const concept = showing ? conceptById(showing) : undefined;
  const text = concept ? HINTS[concept.id] : undefined;

  // **Re-derived every turn, never latched.** The subject is a square, and a
  // square is a fact about a board that moves — the pawn nearest a heap is a
  // different pawn once anybody steps. Held, the ring would sit on the piece
  // that used to be the answer.
  const target = useMemo(
    () => (concept ? hintTargetOf(concept, state, side) : null),
    [concept, state, side]
  );

  return useMemo(
    () => (concept && text ? { concept, text, target, dismiss, hide } : null),
    [concept, text, target, dismiss, hide]
  );
}
